import { supabase } from '@/integrations/supabase/client';

/**
 * Gère les redirections SSO entre sous-domaines
 */
export class SSORedirectManager {
  private static readonly SUBDOMAIN_MAP = {
    'challenge.recut.art': 'https://challenge.recut.art',
    'app.recut.art': 'https://app.recut.art'
  };

  /**
   * Redirige vers un sous-domaine en préservant l'authentification
   */
  static async redirectToSubdomain(targetSubdomain: keyof typeof SSORedirectManager.SUBDOMAIN_MAP, path = '/') {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      console.warn('Pas de session active pour la redirection SSO');
      return;
    }

    const targetUrl = SSORedirectManager.SUBDOMAIN_MAP[targetSubdomain];
    if (!targetUrl) {
      console.error(`Sous-domaine non reconnu: ${targetSubdomain}`);
      return;
    }

    // Construire l'URL de redirection
    const redirectUrl = new URL(path, targetUrl);
    
    // Ajouter des paramètres pour indiquer une redirection SSO si nécessaire
    redirectUrl.searchParams.set('sso_redirect', 'true');
    
    // Effectuer la redirection
    window.location.href = redirectUrl.toString();
  }

  /**
   * Vérifie si l'utilisateur vient d'une redirection SSO
   */
  static isSSORedirect(): boolean {
    if (typeof window === 'undefined') return false;
    
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('sso_redirect') === 'true';
  }

  /**
   * Nettoie les paramètres SSO de l'URL après traitement
   */
  static cleanSSOParams() {
    if (typeof window === 'undefined') return;
    
    const url = new URL(window.location.href);
    url.searchParams.delete('sso_redirect');
    
    // Remplacer l'URL sans recharger la page
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Initialise la gestion SSO pour l'application actuelle
   */
  static initSSO() {
    // Nettoyer les paramètres SSO au chargement
    if (SSORedirectManager.isSSORedirect()) {
      // Attendre que l'auth soit initialisée avant de nettoyer
      setTimeout(() => {
        SSORedirectManager.cleanSSOParams();
      }, 1000);
    }

    // Écouteur pour les messages cross-domain
    window.addEventListener('message', (event) => {
      // Vérifier l'origine pour la sécurité
      if (!event.origin.includes('recut.art')) return;

      if (event.data.type === 'SSO_SESSION_SYNC') {
        const { session } = event.data.payload;
        
        if (session) {
          supabase.auth.setSession(session);
        }
      }
    });
  }

  /**
   * Synchronise manuellement la session avec les autres sous-domaines
   */
  static async syncSessionAcrossSubdomains() {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (session && typeof window !== 'undefined') {
      // Envoyer la session aux autres domaines
      window.postMessage({
        type: 'SSO_SESSION_SYNC',
        payload: { session }
      }, '*');
    }
  }
}

/**
 * Hook pour utiliser les fonctionnalités SSO
 */
export const useSSO = () => {
  return {
    redirectToChallenge: (path?: string) => 
      SSORedirectManager.redirectToSubdomain('challenge.recut.art', path),
    redirectToApp: (path?: string) => 
      SSORedirectManager.redirectToSubdomain('app.recut.art', path),
    syncSession: () => 
      SSORedirectManager.syncSessionAcrossSubdomains(),
    isSSORedirect: () => 
      SSORedirectManager.isSSORedirect()
  };
};